'use client';

import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { AdminFleetView, type AdminTariffs } from '@/components/admin/AdminFleetView';
import { isPlatformHost } from '@/config';
import { api } from '@/lib/api';
import { currentStaff } from '@/lib/auth-client';
import { parseEntitlements, type Entitlements } from '@/lib/entitlements';
import { currentImpersonation } from '@/lib/platform/impersonation';
import { isStaffRole } from '@/lib/roles';
import { tariffCatalogSchema } from '@/lib/booking/tariff-catalog';
import type { AgencyProfile } from '@/lib/agency';

const EMPTY_TARIFFS: AdminTariffs = { rules: [], tiers: [], extras: [] };

interface LoadedConsole {
  agency: AgencyProfile | null;
  tariffs: AdminTariffs;
  entitlements: Entitlements | null;
}

async function loadConsole(): Promise<LoadedConsole> {
  const [agency, tariffs, entitlements] = await Promise.all([
    api<AgencyProfile>('/api/staff/agency').catch(() => null),
    api<unknown>('/api/tariffs')
      .then((payload) => tariffCatalogSchema.parse(payload) as AdminTariffs)
      .catch(() => EMPTY_TARIFFS),
    api<unknown>('/api/entitlements').then(parseEntitlements).catch(() => null),
  ]);

  return { agency, tariffs, entitlements };
}

function ConsoleLoading() {
  return (
    <main className="min-h-screen bg-shell p-6">
      <div className="mx-auto max-w-6xl space-y-3">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="h-12 animate-pulse rounded-2xl bg-card" />
        ))}
      </div>
    </main>
  );
}

export function AdminRoute() {
  const impersonation = currentImpersonation();
  const session = impersonation
    ? { token: impersonation.token, user: impersonation.user }
    : currentStaff();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<LoadedConsole | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    loadConsole()
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // Reload only when the signed-in identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user.id]);

  if (isPlatformHost() && !impersonation) return <Navigate to="/super-admin" replace />;
  if (!session) return <Navigate to="/login" replace />;
  if (!isStaffRole(session.user.role)) return <Navigate to="/login?error=platform" replace />;

  if (failed) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 px-6 text-center text-sm text-slate-300">
        La console n'a pas pu charger les données de l'agence.
      </main>
    );
  }

  if (!data) return <ConsoleLoading />;

  return (
    <AdminFleetView
      initialVehicles={[]}
      initialBookings={[]}
      initialClients={[]}
      initialExpenses={[]}
      initialAgency={data.agency}
      initialTariffs={data.tariffs}
      initialFines={[]}
      initialMovements={[]}
      impersonation={impersonation ? { agencyName: impersonation.agencyName } : null}
      sessionRole={session.user.role}
      operatorName={session.user.name}
      operatorEmail={session.user.email}
      fleetUnavailable={false}
      listCaps={[]}
      entitlements={data.entitlements}
      jsonImportAllowed={import.meta.env.DEV}
      initialSection={searchParams.get('section') ?? undefined}
    />
  );
}
